/**
 * As ações do dono que mais de uma tela oferece (spec 23, jornada 4): reconectar no canal certo, pausar o que sai
 * por um número em risco, e a aba endereçável por `?aba=`.
 *
 * Elas moram aqui, e não em cada tela, porque o Início, Canais e a Saúde do número oferecem o MESMO verbo — e verbo
 * escrito três vezes só é igual até alguém mudar um deles.
 */

import { useState, type ReactNode } from "react";
import { useLocation, useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import "./acoes-do-dono.css";
import { PauseCircle } from "lucide-react";
import { useSessao } from "@hugi/ui";
import { Dialogo } from "@hugi/ui/kit";
import { agirNaPublicacao, pausarCampanha, useCampanhas, usePublicacoes, type Campanha, type Publicacao } from "./dados.js";

/**
 * Onde se reconecta uma conexão: o canal DELA, já aberto no passo (`/canais/conectar?canal=…`). "Reconectar" que cai
 * na lista de canais faz a pessoa escolher de novo o que já tinha escolhido — e o número por QR escolhido como
 * oficial é um número diferente.
 */
export function canalDeConectar(canal: string | null | undefined, modo: string | null | undefined): string {
  const id =
    canal === "whatsapp"
      ? modo === "nao_oficial"
        ? "whatsapp_qr"
        : "whatsapp_oficial"
      : canal === "telegram" || canal === "instagram" || canal === "facebook"
        ? canal
        : null;
  return id ? `/canais/conectar?canal=${id}` : "/canais/conectar";
}

/**
 * A aba da tela, lida de `?aba=` e gravada lá ao trocar (spec 23: o Início manda para `/ajustes?aba=plano`). Valor
 * desconhecido cai no padrão — link velho não quebra a tela. `replace`: trocar de aba não empilha histórico.
 */
export function useAbaDaUrl<T extends string>(validas: readonly T[], padrao: T): [T, (aba: T) => void] {
  const busca = useSearch();
  const [caminho, navegar] = useLocation();
  const pedida = new URLSearchParams(busca).get("aba") as T | null;
  const aba = pedida && validas.includes(pedida) ? pedida : padrao;
  const trocar = (nova: T) => {
    const q = new URLSearchParams(busca);
    q.set("aba", nova);
    navegar(`${caminho}?${q.toString()}`, { replace: true });
  };
  return [aba, trocar];
}

/** O que ainda vai SAIR por este número: campanha agendada ou enviando, publicação agendada ou executando. */
export function ativasDoNumero(
  conexaoId: string,
  campanhas: readonly Campanha[],
  publicacoes: readonly Publicacao[],
): { readonly campanhas: readonly Campanha[]; readonly publicacoes: readonly Publicacao[] } {
  return {
    campanhas: campanhas.filter((k) => k.conexaoId === conexaoId && (k.estado === "agendada" || k.estado === "enviando")),
    publicacoes: publicacoes.filter((p) => p.conexaoId === conexaoId && (p.estado === "agendada" || p.estado === "executando")),
  };
}

/** Quem pode pausar alguma coisa: `send_campaign` pausa campanha, `manage_post` pausa publicação — as rotas exigem isso. */
export function podePausarAlgo(permissoes: readonly string[]): boolean {
  return permissoes.includes("send_campaign") || permissoes.includes("manage_post");
}

/**
 * "Pausar campanhas deste número" — o verbo do aviso de saúde (spec 23, jornada 4).
 *
 * Só aparece para quem tem a permissão da rota, e só quando há o que pausar: botão que não pausa nada é promessa
 * vazia. Pede confirmação no `Dialogo`, dizendo O QUE para; pausar é reversível (cada campanha e publicação tem
 * "Retomar" na própria tela), e por isso a confirmação é curta.
 *
 * Quando nada se aplica, mostra `senao` — o caminho alternativo de quem chama ("Ver saúde").
 */
interface PropsDoPausar {
  readonly conexaoId: string;
  readonly classe?: string;
  readonly rotulo?: string;
  /** O que aparece no lugar quando não há o que pausar, ou falta a permissão (o caminho alternativo). */
  readonly senao?: ReactNode;
}

export function PausarDoNumero(props: PropsDoPausar) {
  const sessao = useSessao();
  if (!podePausarAlgo(sessao.permissoes)) return <>{props.senao ?? null}</>;
  return <PausarComPermissao {...props} />;
}

function PausarComPermissao(props: PropsDoPausar) {
  const sessao = useSessao();
  const cliente = useQueryClient();
  const podeCampanha = sessao.permissoes.includes("send_campaign");
  const podePublicacao = sessao.permissoes.includes("manage_post");
  const campanhas = useCampanhas();
  const publicacoes = usePublicacoes();
  const [aberto, setAberto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [falhas, setFalhas] = useState<string[]>([]);
  const [feito, setFeito] = useState<string | null>(null);

  const alvo = ativasDoNumero(
    props.conexaoId,
    podeCampanha ? (campanhas.data?.itens ?? []) : [],
    podePublicacao ? (publicacoes.data?.itens ?? []) : [],
  );
  const total = alvo.campanhas.length + alvo.publicacoes.length;
  if (feito) return <span className="hugi-acao-feita" role="status">{feito}</span>;
  if (total === 0) return <>{props.senao ?? null}</>;

  async function pausar() {
    setOcupado(true);
    const erros: string[] = [];
    for (const k of alvo.campanhas) {
      const r = await pausarCampanha(cliente, k.id);
      if (!r.ok) erros.push(`${k.nome}: ${r.falha?.erro.mensagem ?? "não pausou"}`);
    }
    for (const p of alvo.publicacoes) {
      const r = await agirNaPublicacao(cliente, p.id, "pausar", { motivo: "saúde do número" });
      if (!r.ok) erros.push(`${p.nome}: ${r.falha?.erro.mensagem ?? "não pausou"}`);
    }
    setOcupado(false);
    setFalhas(erros);
    if (erros.length === 0) {
      setAberto(false);
      setFeito(total === 1 ? "Pausada" : `${total} pausadas`);
    }
  }

  const partes = [
    alvo.campanhas.length ? `${alvo.campanhas.length} ${alvo.campanhas.length === 1 ? "campanha" : "campanhas"}` : null,
    alvo.publicacoes.length ? `${alvo.publicacoes.length} ${alvo.publicacoes.length === 1 ? "publicação" : "publicações"}` : null,
  ].filter(Boolean);

  return (
    <>
      <button type="button" className={props.classe ?? "v2-botao is-pequeno"} onClick={() => setAberto(true)}>
        <PauseCircle size={14} aria-hidden="true" /> {props.rotulo ?? "Pausar campanhas deste número"}
      </button>
      <Dialogo
        aberto={aberto}
        aoFechar={() => setAberto(false)}
        titulo="Pausar o que sai por este número?"
        largura="estreita"
        rodape={
          <>
            <button type="button" className="v2-botao" onClick={() => setAberto(false)} disabled={ocupado}>Cancelar</button>
            <button type="button" className="v2-botao is-primario" onClick={() => void pausar()} disabled={ocupado}>
              {ocupado ? "Pausando…" : `Pausar ${partes.join(" e ")}`}
            </button>
          </>
        }
      >
        <ul className="hugi-pausar-lista">
          {alvo.campanhas.map((k) => <li key={`k-${k.id}`}>{k.nome}</li>)}
          {alvo.publicacoes.map((p) => <li key={`p-${p.id}`}>{p.nome}</li>)}
        </ul>
        <p className="hugi-pausar-nota">Volta quando você retomar, na tela de cada uma.</p>
        {falhas.length ? (
          <ul role="alert" className="hugi-pausar-falhas">
            {falhas.map((f) => <li key={f}>{f}</li>)}
          </ul>
        ) : null}
      </Dialogo>
    </>
  );
}
